import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import { Input } from '~/components/common/CurieInput'
import { useAppConfig } from '~/contexts/AppConfigContext'
import { useUserContext } from '~/contexts/UserContext'
import { usePlanSelection } from '~/hooks/usePlanSelection'
import { useUser } from '~/hooks/user/useUser'
import { trace } from '~/services/analyticsService'
import { performLogout } from '~/services/bridge/pluginAuth'
import { sendVerificationCode } from '~/services/userService'
import { BetterToast } from '~/utils/toast'
import { Separator } from '../common/Separator'

const signupVerifyEmail = async (email: string, code: string, plan: string = 'flex') => {
  const { resolveApiUrl } = await import('~/utils/apiUrl')
  const response = await fetch(resolveApiUrl('/api/user/email/verify'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password: null, code, plan })
  })
  if (!response.ok) {
    const responeJson = await response.json()
    const reason = responeJson.detail
    throw new Error(`Failed to verify email. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const EmailVerificationForm = () => {
  const { config, loading: configLoading } = useAppConfig()
  const { refetch } = useUserContext()
  const user = useUser()
  const [isDisabled, setIsDisabled] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [verificationCodeSent, setVerificationCodeSent] = useState(false)
  const navigate = useNavigate()
  const { selectedPlan: plan } = usePlanSelection()

  // Get email from user context instead of URL parameters
  const email = user?.email || ''

  useEffect(() => {
    const setupEmailVerification = async () => {
      // Wait for config to load before making decisions
      if (configLoading) return

      const url = new URL(window.location.href)
      const returnUrlParam = url.searchParams.get('return_url')

      // Clean up URL and set query params
      url.searchParams.delete('email') // Remove legacy email param if present
      setSearchParams(url.searchParams.toString())

      // If no email from user context, logout and redirect to signin
      if (!email) {
        BetterToast.error('Please sign in first')
        performLogout('/signin')
        return
      }

      // Check if already verified (from user context)
      if (user?.email_verified) {
        if (returnUrlParam) {
          window.location.href = returnUrlParam
        } else {
          navigate(`/?${searchParams}`)
        }
        return
      }

      // Only send verification code once and only when email verification is enabled
      if (config.email_verification_enabled && !verificationCodeSent) {
        try {
          const response = await sendVerificationCode(email)
          if (response) {
            BetterToast.success('Verification code sent to your email')
            setVerificationCodeSent(true)
          }
        } catch (err) {
          const error = err as Error
          BetterToast.error(error.message)
        }
      }
    }

    setupEmailVerification()
  }, [
    config,
    configLoading,
    navigate,
    email,
    searchParams,
    user?.email_verified,
    setSearchParams,
    verificationCodeSent
  ])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!verificationCode) {
      return BetterToast.error('Please enter verification code')
    }

    try {
      setIsDisabled(true)
      await signupVerifyEmail(email, verificationCode, plan)

      // Refresh user data to get updated verification status
      await refetch()

      trace({
        event: 'signup_completed',
        labels: {
          status: 'success',
          plan: plan
        }
      })
      BetterToast.success('Email verified! You may now continue.')
      const returnUrl = searchParams.get('return_url')
      if (returnUrl) {
        // Decode and redirect to the original destination
        const decodedReturnUrl = decodeURIComponent(returnUrl)
        window.location.href = decodedReturnUrl
      } else {
        // Let the centralized verification flow handle the next step
        const query = searchParams.toString()
        navigate('/' + query)
      }
    } catch (err) {
      const error = err as Error
      BetterToast.error(error.message)
      trace({
        event: 'signup_completed',
        labels: {
          status: 'failed',
          reason: error.message,
          plan: plan
        }
      })
      setIsDisabled(false)
    }
  }

  const handleResendCode = async () => {
    try {
      setIsDisabled(true)
      await sendVerificationCode(email)
      BetterToast.success('Verification code resent')
    } catch (err) {
      const error = err as Error
      BetterToast.error(error.message)
    } finally {
      setIsDisabled(false)
    }
  }

  const handleSignInClick = async () => {
    await performLogout('/signin')
  }

  const handleForgotPasswordClick = async () => {
    await performLogout('/reset-password')
  }

  return (
    <div className='flex-1 flex flex-col justify-center items-center w-full h-full pb-4'>
      <div className='w-full max-w-[450px] p-5 pt-24'>
        <h1
          aria-label='Verification title'
          className='mb-[40px] text-center font-semibold text-2xl select-none'
        >
          Verify your email
        </h1>

        <form id='signupVerificationForm' className='space-y-6' onSubmit={handleSubmit}>
          <div className='text-center text-gray-600 mb-6 select-none'>
            We&apos;ve sent a verification code to {email}
          </div>
          <div>
            <Input
              type='text'
              id='verificationCode'
              placeholder='Enter verification code'
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
              autoComplete='off'
            />
          </div>
          <Button
            variant='primary'
            id='verify-email'
            type='submit'
            className='mx-0 mt-4 h-8 w-full'
            isDisabled={isDisabled}
          >
            Create account
          </Button>
          <div className='text-center'>
            <button
              type='button'
              onClick={handleResendCode}
              className='text-blue-600 hover:text-blue-800 select-none'
              disabled={isDisabled}
            >
              Resend verification code
            </button>
          </div>
        </form>
        <Separator text='or' className='mt-1' />
        <div className='margin-auto'>
          <div className='mt-1 w-full flex justify-center gap-1 text-[rgba(0,0,0,0.5)] select-none'>
            Already have an account?
            <button
              type='button'
              onClick={handleSignInClick}
              className='text-blue-600 hover:text-blue-800 underline bg-transparent border-none cursor-pointer p-0 font-inherit'
            >
              Sign In
            </button>
          </div>
          <div className='mt-1 w-full flex justify-center text-[rgba(0,0,0,0.5)] select-none'>
            <button
              type='button'
              onClick={handleForgotPasswordClick}
              className='text-blue-600 hover:text-blue-800 underline bg-transparent border-none cursor-pointer p-0 font-inherit'
            >
              Forgot password?
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailVerificationForm
